import { useEffect, useMemo, useState } from "react";
import { taskService } from "../../services/taskService";
import Icon from "../../components/Common/Icon";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import { useToast } from "../../context/ToastContext";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString("tr-TR", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }) : null;

const formatForInput = (d) => (d ? d.slice(0, 16) : "");

const emptyForm = { title: "", description: "", startDate: "", dueDate: "" };

function TaskRow({ task, onToggle, onOpenDetail }) {
  const done = task.status === "DONE";

  return (
    <div
      onClick={() => onOpenDetail(task)}
      className="flex items-center gap-3 px-4 py-3.5 border-b border-outline-variant/60 hover:bg-surface-container-low/50 transition group cursor-pointer"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(task); }}
        title={done ? "Yapılacak olarak işaretle" : "Tamamlandı olarak işaretle"}
        className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition ${done ? "bg-primary border-primary text-white" : "border-outline-variant hover:border-primary"
          }`}
      >
        {done && <Icon name="check" className="!text-[15px]" />}
      </button>

      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${done ? "line-through text-on-surface-variant" : "text-on-surface"}`}>
          {task.title}
        </p>
      </div>

      {task.dueDate && (
        <span className="hidden sm:flex items-center gap-1 text-xs text-on-surface-variant shrink-0">
          <Icon name="schedule" className="!text-[16px]" /> {fmtDate(task.dueDate)}
        </span>
      )}

      <button
        onClick={(e) => { e.stopPropagation(); onOpenDetail(task); }}
        title="Detay"
        className="p-1.5 rounded-lg text-on-surface-variant hover:bg-surface-container-high hover:text-primary transition shrink-0"
      >
        <Icon name="more_vert" className="!text-[18px]" />
      </button>
    </div>
  );
}

/* --------------------------------------------------------- Görev Detay ekranı */

function TaskDetailModal({ task, onClose, onToggle, onEdit, onDelete }) {
  const done = task.status === "DONE";
  return (
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-surface-card w-full max-w-lg rounded-2xl shadow-modal overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
          <h3 className="text-lg font-bold flex items-center gap-2"><Icon name="task_alt" className="text-primary" /> Görev Detayı</h3>
          <button onClick={onClose} className="p-2 hover:bg-surface-container-high rounded-full"><Icon name="close" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5 text-sm">
          <div className="flex items-start justify-between gap-3">
            <h4 className={`text-xl font-bold ${done ? "line-through text-on-surface-variant" : "text-on-surface"}`}>{task.title}</h4>
            <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider shrink-0 ${done ? "bg-success/15 text-success" : "bg-blue-500/15 text-blue-600"
              }`}>{done ? "Tamamlandı" : "Yapılacak"}</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-surface-container-low p-3 rounded-xl">
              <p className="text-[11px] text-on-surface-variant mb-1">Başlangıç</p>
              <p className="font-bold">{fmtDate(task.startDate) ?? "—"}</p>
            </div>
            <div className="bg-surface-container-low p-3 rounded-xl">
              <p className="text-[11px] text-on-surface-variant mb-1">Bitiş</p>
              <p className="font-bold">{fmtDate(task.dueDate) ?? "—"}</p>
            </div>
          </div>

          <div>
            <h5 className="font-bold mb-2 text-xs uppercase tracking-wider text-on-surface-variant">Tanım</h5>
            {task.description ? (
              <div className="p-4 bg-surface-container-highest/30 rounded-xl text-on-surface whitespace-pre-wrap">{task.description}</div>
            ) : (
              <p className="text-on-surface-variant italic">Tanım eklenmemiş.</p>
            )}
          </div>
        </div>

        <div className="p-5 border-t border-outline-variant bg-surface-container-low space-y-2">
          <button onClick={() => onToggle(task)} className={`w-full py-2.5 font-semibold rounded-xl transition text-sm flex items-center justify-center gap-1.5 ${done ? "border border-outline-variant hover:bg-surface-container-high" : "bg-primary text-white hover:opacity-90 shadow-lg"
            }`}>
            <Icon name={done ? "undo" : "check"} className="!text-[18px]" /> {done ? "Yapılacaklara Al" : "Tamamlandı İşaretle"}
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => onEdit(task)} className="py-2.5 border border-outline-variant rounded-xl font-semibold text-sm hover:bg-surface-container-high transition flex items-center justify-center gap-1.5"><Icon name="edit" className="!text-[18px]" /> Düzenle</button>
            <button onClick={() => onDelete(task)} className="py-2.5 border border-error/30 text-error rounded-xl font-semibold text-sm hover:bg-error/10 transition flex items-center justify-center gap-1.5"><Icon name="delete" className="!text-[18px]" /> Sil</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TasksPage() {
  const { showToast } = useToast();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });
  const [detail, setDetail] = useState(null);
  const [limit, setLimit] = useState(null); // { limit, used, remaining }

  const loadLimit = () => {
    taskService.limit().then((r) => setLimit(r.data?.data ?? null)).catch(() => { });
  };

  const load = () => {
    setLoading(true);
    taskService
      .list()
      .then((res) => setTasks(res.data?.data ?? []))
      .catch(() => showToast("Görevler yüklenemedi.", "error"))
      .finally(() => setLoading(false));
    loadLimit();
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const todo = useMemo(() => tasks.filter((t) => t.status !== "DONE"), [tasks]);
  const done = useMemo(() => tasks.filter((t) => t.status === "DONE"), [tasks]);

  const limitReached = limit && limit.limit != null && limit.remaining <= 0;

  const openCreate = () => {
    if (limitReached) {
      showToast(`Salon olarak görev limitiniz tüketildi (${limit.limit}). Daha fazla görev için paketi yükseltin.`, "error");
      return;
    }
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
    setModalOpen(true);
  };
  const openEdit = (t) => {
    setDetail(null);
    setEditingId(t.id);
    setForm({
      title: t.title,
      description: t.description ?? "",
      startDate: formatForInput(t.startDate),
      dueDate: formatForInput(t.dueDate),
    });
    setFormError(null);
    setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); setEditingId(null); };

  const toggleStatus = async (t) => {
    const next = t.status === "DONE" ? "TODO" : "DONE";
    // İyimser güncelleme
    setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, status: next } : x)));
    setDetail((d) => (d && d.id === t.id ? { ...d, status: next } : d));
    try {
      await taskService.setStatus(t.id, next);
    } catch (err) {
      setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, status: t.status } : x)));
      setDetail((d) => (d && d.id === t.id ? { ...d, status: t.status } : d));
      showToast(err.response?.data?.error ?? "Durum güncellenemedi.", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!form.title.trim()) { setFormError("Görev adı zorunludur."); return; }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      startDate: form.startDate || null,
      dueDate: form.dueDate || null,
    };
    try {
      if (editingId) { await taskService.update(editingId, payload); showToast("Görev güncellendi.", "success"); }
      else { await taskService.create(payload); showToast("Görev oluşturuldu.", "success"); }
      closeModal();
      load();
    } catch (err) {
      setFormError(err.response?.data?.error ?? "İşlem başarısız.");
    } finally { setSaving(false); }
  };

  const triggerDelete = (t) => {
    setDetail(null);
    setConfirmModal({
      isOpen: true,
      title: "Görevi Sil",
      message: `"${t.title}" görevi silinecek. Emin misiniz?`,
      type: "danger",
      onConfirm: () =>
        taskService.remove(t.id)
          .then(() => { showToast("Görev silindi.", "success"); load(); })
          .catch((err) => showToast(err.response?.data?.error ?? "İşlem başarısız.", "error")),
    });
  };

  const Section = ({ title, items, emptyText }) => (
    <section>
      <h3 className="text-sm font-bold text-on-surface-variant px-4 py-2">{title} <span className="text-on-surface-variant/60 font-medium">({items.length})</span></h3>
      <div className="bg-surface-card rounded-2xl border border-outline-variant overflow-hidden">
        {items.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-on-surface-variant">{emptyText}</p>
        ) : (
          items.map((t) => (
            <TaskRow key={t.id} task={t} onToggle={toggleStatus} onOpenDetail={setDetail} />
          ))
        )}
      </div>
    </section>
  );

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="checklist" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Görev Listesi</h1>
            <p className="text-on-surface-variant text-sm">Kişisel yapılacaklar listen.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {limit && limit.limit != null && (
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${limitReached ? "bg-error/10 text-error" : "bg-surface-container-high text-on-surface-variant"
              }`}>
              Salon Bakiyesi: {limit.used}/{limit.limit}
            </span>
          )}
          <button
            onClick={openCreate}
            disabled={limitReached}
            title={limitReached ? "Salon görev limiti doldu" : "Görev oluştur"}
            className="flex items-center gap-1.5 border border-primary text-primary hover:bg-primary/5 rounded-full px-5 py-2 font-semibold text-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Icon name="add" className="!text-[18px]" /> Görev Oluştur
          </button>
        </div>
      </div>

      {limitReached && (
        <div className="flex items-center gap-2 text-sm text-error bg-error/10 border border-error/20 rounded-xl px-4 py-3">
          <Icon name="error" className="!text-[18px]" />
          Salon olarak görev limitiniz tüketildi ({limit.limit}). Daha fazla görev için paketi yükseltin.
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-on-surface-variant">Yükleniyor…</div>
      ) : (
        <div className="space-y-6">
          <Section title="Yapılacaklar" items={todo} emptyText="Bekleyen görev yok." />
          <Section title="Tamamlanmış" items={done} emptyText="Henüz tamamlanan görev yok." />
        </div>
      )}

      {/* Görev detay ekranı */}
      {detail && (
        <TaskDetailModal
          task={detail}
          onClose={() => setDetail(null)}
          onToggle={toggleStatus}
          onEdit={openEdit}
          onDelete={triggerDelete}
        />
      )}

      {/* Görev Oluştur / Düzenle modalı */}
      {modalOpen && (
        <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-surface-card w-full max-w-xl rounded-2xl shadow-modal overflow-hidden max-h-[92vh] flex flex-col">
            <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
              <h3 className="text-lg font-bold">{editingId ? "Görevi Düzenle" : "Görev Oluştur"}</h3>
              <button onClick={closeModal} className="p-2 hover:bg-surface-container-high rounded-full"><Icon name="close" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto text-sm">
              {formError && (
                <div className="text-xs text-error bg-error/10 border border-error/20 rounded-xl px-3 py-2 flex items-center gap-1.5">
                  <Icon name="error" className="!text-[16px]" /> {formError}
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Görev Adı *</label>
                <input
                  autoFocus
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Başlık"
                  className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2.5 outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Tanım</label>
                <textarea
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Görev detayları…"
                  className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2.5 outline-none focus:border-primary resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Başlangıç Tarihi & Saati</label>
                  <input type="datetime-local" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2.5 outline-none focus:border-primary" />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Bitiş Tarihi & Saati</label>
                  <input type="datetime-local" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                    className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2.5 outline-none focus:border-primary" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={closeModal} className="flex items-center gap-1.5 px-5 py-2.5 font-semibold text-on-surface-variant hover:bg-surface-container-high rounded-xl transition text-sm">
                  <Icon name="close" className="!text-[18px]" /> İptal
                </button>
                <button type="submit" disabled={saving} className="flex items-center gap-1.5 px-6 py-2.5 bg-primary text-white font-semibold rounded-xl shadow-lg hover:opacity-90 transition disabled:opacity-50 text-sm">
                  <Icon name="check" className="!text-[18px]" /> {saving ? "Kaydediliyor…" : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
